import React, { useState, useEffect } from 'react';
import {
  Filter, Search, Eye, Calendar, User, Database, Activity,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight
} from 'lucide-react';
import { styles as themeStyles, Modal } from './theme';

const AuditLogs = ({ token, API_URL, fetchWithAuth }) => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    user_id: '',
    table_name: '',
    action: '',
    start_date: '',
    end_date: '',
    search: ''
  });
  const [viewingLog, setViewingLog] = useState(null);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    fetchLogs(currentPage, perPage);
    fetchStats();
  }, [filters, currentPage, perPage]);

  const fetchLogs = async (page = 1, limit = 10) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page,
        per_page: limit,
        ...(filters.user_id && { user_id: filters.user_id }),
        ...(filters.table_name && { table_name: filters.table_name }),
        ...(filters.action && { action: filters.action }),
        ...(filters.start_date && { start_date: filters.start_date }),
        ...(filters.end_date && { end_date: filters.end_date }),
        ...(filters.search && { search: filters.search })
      });

      const res = await fetchWithAuth(`${API_URL}/audit-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setLogs(data.logs || []);
      setTotalCount(data.total || 0);
      setTotalPages(Math.ceil((data.total || 0) / limit));
    } catch (err) {
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/audit-logs/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      user_id: '',
      table_name: '',
      action: '',
      start_date: '',
      end_date: '',
      search: ''
    });
    setCurrentPage(1);
  };

  const getActionColor = (action) => {
    const colors = {
      CREATE: '#10b981',
      UPDATE: '#f59e0b',
      DELETE: '#ef4444',
      APPROVE: '#047857',
      REJECT: '#b91c1c',
      SECURITY_ALERT: '#e67e22',
      VIEW: '#3b82f6',
      LOGIN: '#8b5cf6',
      LOGOUT: '#6b7280'
    };
    return colors[action] || '#6b7280';
  };

  // Local overrides or specific styles for AuditLogs
  const localStyles = {
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '24px'
    },
    statCard: {
      ...themeStyles.card,
      padding: '20px',
      borderLeft: '4px solid',
      marginBottom: 0
    },
    filterGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '12px',
      marginBottom: '16px'
    }
  };

  return (
    <div style={themeStyles.mainContent}>
      <div style={themeStyles.header}>
        <h1 style={themeStyles.title}>Audit Logs</h1>
      </div>

      {/* Statistics */}
      {stats && (
        <div style={localStyles.statsGrid}>
          <div style={{ ...localStyles.statCard, borderLeftColor: '#3b82f6' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <Activity style={{ width: '24px', height: '24px', color: '#3b82f6' }} />
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Total Actions</span>
            </div>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937' }}>
              {stats.actions_by_type?.reduce((sum, item) => sum + item.count, 0) || 0}
            </div>
          </div>

          <div style={{ ...localStyles.statCard, borderLeftColor: '#10b981' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <Database style={{ width: '24px', height: '24px', color: '#10b981' }} />
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Tables Affected</span>
            </div>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937' }}>
              {stats.actions_by_table?.length || 0}
            </div>
          </div>

          <div style={{ ...localStyles.statCard, borderLeftColor: '#f59e0b' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <User style={{ width: '24px', height: '24px', color: '#f59e0b' }} />
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Active Users</span>
            </div>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937' }}>
              {stats.actions_by_user?.length || 0}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={themeStyles.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Filter style={{ width: '20px', height: '20px', color: '#6b7280' }} />
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>Filters</h2>
        </div>

        <div style={localStyles.filterGrid}>
          <input
            type="text"
            placeholder="Search username, table, action..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            style={themeStyles.input}
          />

          <select
            value={filters.action}
            onChange={(e) => handleFilterChange('action', e.target.value)}
            style={themeStyles.input}
          >
            <option value="">All Actions</option>
            <option value="CREATE">Create</option>
            <option value="UPDATE">Update</option>
            <option value="DELETE">Delete</option>
            <option value="APPROVE">Approve</option>
            <option value="REJECT">Reject</option>
            <option value="VIEW">View</option>
            <option value="LOGIN">Login</option>
            <option value="LOGOUT">Logout</option>
          </select>

          <select
            value={filters.table_name}
            onChange={(e) => handleFilterChange('table_name', e.target.value)}
            style={themeStyles.input}
          >
            <option value="">All Tables</option>
            <option value="chart_of_accounts">Chart of Accounts</option>
            <option value="purchase_orders">Purchase Orders</option>
            <option value="items">Items</option>
            <option value="suppliers">Suppliers</option>
            <option value="customers">Customers</option>
            <option value="users">Users</option>
            <option value="disbursement_vouchers">Disbursement Vouchers</option>
            <option value="journal_vouchers">Journal Vouchers</option>
            <option value="receiving_entries">Receiving Entries</option>
          </select>

          <input
            type="date"
            placeholder="Start Date"
            value={filters.start_date}
            onChange={(e) => handleFilterChange('start_date', e.target.value)}
            style={themeStyles.input}
          />

          <input
            type="date"
            placeholder="End Date"
            value={filters.end_date}
            onChange={(e) => handleFilterChange('end_date', e.target.value)}
            style={themeStyles.input}
          />
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => fetchLogs(1, perPage)} style={themeStyles.button}>
            <Search style={{ width: '16px', height: '16px' }} />
            Apply Filters
          </button>
          <button onClick={clearFilters} style={{ ...themeStyles.button, ...themeStyles.buttonSecondary }}>
            Clear All
          </button>
        </div>
      </div>

      {/* Logs Table */}
      <div style={{ ...themeStyles.card, padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
            Loading audit logs...
          </div>
        ) : (
          <>
            <table style={themeStyles.table}>
              <thead>
                <tr>
                  <th style={themeStyles.th}>Timestamp</th>
                  <th style={themeStyles.th}>User</th>
                  <th style={themeStyles.th}>Action</th>
                  <th style={themeStyles.th}>Table</th>
                  <th style={themeStyles.th}>Record ID</th>
                  <th style={themeStyles.th}>IP Address</th>
                  <th style={{ ...themeStyles.th, textAlign: 'right' }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id}>
                    <td style={themeStyles.td}>
                      <div style={{ fontSize: '13px', color: '#374151' }}>
                        {new Date(log.created_at).toLocaleString()}
                      </div>
                    </td>
                    <td style={themeStyles.td}>
                      <div style={{ fontWeight: '600' }}>{log.username}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>ID: {log.user_id}</div>
                    </td>
                    <td style={themeStyles.td}>
                      <span
                        style={{
                          ...themeStyles.badge,
                          backgroundColor: ['APPROVE', 'REJECT', 'SECURITY_ALERT'].includes(log.action) ? getActionColor(log.action) : getActionColor(log.action) + '20',
                          color: ['APPROVE', 'REJECT', 'SECURITY_ALERT'].includes(log.action) ? 'white' : getActionColor(log.action),
                          border: `1px solid ${getActionColor(log.action)}`
                        }}
                      >
                        <div>{log.action}</div>
                        {log.action === 'SECURITY_ALERT' && log.new_values?.reasons && (
                          <div style={{ fontSize: '10px', marginTop: '4px', borderTop: '1px solid rgba(255,255,255,0.3)', paddingTop: '2px' }}>
                            {log.new_values.reasons.join(', ')}
                          </div>
                        )}
                      </span>
                    </td>
                    <td style={themeStyles.td}>
                      <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                        {log.table_name}
                      </span>
                    </td>
                    <td style={themeStyles.td}>
                      {log.record_id || '—'}
                    </td>
                    <td style={themeStyles.td}>
                      <span style={{ fontSize: '13px', color: '#6b7280' }}>
                        {log.ip_address || '—'}
                      </span>
                    </td>
                    <td style={{ ...themeStyles.td, textAlign: 'right' }}>
                      <button
                        onClick={() => setViewingLog(log)}
                        style={themeStyles.iconButton}
                      >
                        <Eye style={{ width: '16px', height: '16px', color: '#4f46e5' }} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {logs.length === 0 && (
              <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
                No audit logs found
              </div>
            )}

            {/* Pagination */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px',
              backgroundColor: 'white',
              borderTop: '1px solid #e5e7eb'
            }}>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Showing {totalCount === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalCount)} to {Math.min(currentPage * perPage, totalCount)} of {totalCount} entries
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '16px' }}>
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>Rows per page:</span>
                  <select
                    value={perPage}
                    onChange={(e) => {
                      setPerPage(parseInt(e.target.value));
                      setCurrentPage(1);
                    }}
                    style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px'
                    }}
                  >
                    {[10, 25, 50, 100].map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    style={{
                      ...themeStyles.iconButton,
                      padding: '6px',
                      opacity: currentPage === 1 ? 0.5 : 1,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      border: '1px solid #d1d5db'
                    }}
                  >
                    <ChevronsLeft size={16} />
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    style={{
                      ...themeStyles.iconButton,
                      padding: '6px',
                      opacity: currentPage === 1 ? 0.5 : 1,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      border: '1px solid #d1d5db'
                    }}
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <div style={{ display: 'flex', gap: '4px', margin: '0 8px' }}>
                    {[...Array(totalPages || 0)].map((_, i) => {
                      const pageNum = i + 1;
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '4px',
                              border: '1px solid #d1d5db',
                              backgroundColor: currentPage === pageNum ? '#4f46e5' : 'white',
                              color: currentPage === pageNum ? 'white' : '#374151',
                              fontSize: '14px',
                              cursor: 'pointer'
                            }}
                          >
                            {pageNum}
                          </button>
                        );
                      } else if (
                        (pageNum === 2 && currentPage > 3) ||
                        (pageNum === totalPages - 1 && currentPage < totalPages - 2)
                      ) {
                        return <span key={pageNum} style={{ padding: '6px' }}>...</span>;
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages || totalPages === 0}
                    style={{
                      ...themeStyles.iconButton,
                      padding: '6px',
                      opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1,
                      cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                      border: '1px solid #d1d5db'
                    }}
                  >
                    <ChevronRight size={16} />
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages || totalPages === 0}
                    style={{
                      ...themeStyles.iconButton,
                      padding: '6px',
                      opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1,
                      cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                      border: '1px solid #d1d5db'
                    }}
                  >
                    <ChevronsRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* View Log Details Modal */}
      <Modal
        isOpen={!!viewingLog}
        onClose={() => setViewingLog(null)}
        title="Audit Log Details"
        maxWidth="800px"
      >
        {viewingLog && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', fontSize: '14px' }}>
                <strong>Timestamp:</strong>
                <span>{new Date(viewingLog.created_at).toLocaleString()}</span>

                <strong>User:</strong>
                <span>{viewingLog.username} (ID: {viewingLog.user_id})</span>

                <strong>Action:</strong>
                <span style={{
                  ...themeStyles.badge,
                  backgroundColor: ['APPROVE', 'REJECT', 'SECURITY_ALERT'].includes(viewingLog.action) ? getActionColor(viewingLog.action) : getActionColor(viewingLog.action) + '20',
                  color: ['APPROVE', 'REJECT', 'SECURITY_ALERT'].includes(viewingLog.action) ? 'white' : getActionColor(viewingLog.action),
                  border: `1px solid ${getActionColor(viewingLog.action)}`
                }}>
                  <div>{viewingLog.action}</div>
                  {viewingLog.action === 'SECURITY_ALERT' && viewingLog.new_values?.reasons && (
                    <div style={{ fontSize: '11px', marginTop: '4px', borderTop: '1px solid rgba(255,255,255,0.3)', paddingTop: '2px' }}>
                      {viewingLog.new_values.reasons.join(', ')}
                    </div>
                  )}
                </span>

                <strong>Table:</strong>
                <span style={{ fontFamily: 'monospace' }}>{viewingLog.table_name}</span>

                <strong>Record ID:</strong>
                <span>{viewingLog.record_id || 'N/A'}</span>

                <strong>IP Address:</strong>
                <span>{viewingLog.ip_address || 'N/A'}</span>
              </div>
            </div>

            <div style={themeStyles.grid2}>
              {viewingLog.old_values && (
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px', color: '#ef4444' }}>
                    Old Values
                  </h3>
                  <pre style={{
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fee2e2',
                    padding: '12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px'
                  }}>
                    {JSON.stringify(viewingLog.old_values, null, 2)}
                  </pre>
                </div>
              )}

              {viewingLog.new_values && (
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px', color: '#10b981' }}>
                    New Values
                  </h3>
                  <pre style={{
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #dcfce7',
                    padding: '12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px'
                  }}>
                    {JSON.stringify(viewingLog.new_values, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            {viewingLog.user_agent && (
              <div style={{ marginBottom: '16px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
                  User Agent
                </h3>
                <div style={{
                  backgroundColor: '#f3f4f6',
                  padding: '12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  wordBreak: 'break-all',
                  color: '#6b7280'
                }}>
                  {viewingLog.user_agent}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={() => setViewingLog(null)}
                style={themeStyles.button}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AuditLogs;